import { LocationFeatureDto } from '@flyfreely-portal-ui/flyfreely';
import { groupByMapFeature } from '@flyfreely-portal-ui/locations';
import { FeatureGroup } from '@flyfreely-portal-ui/map';

/**
 * Build map FeatureGroups specific to LOTAMs
 * @param locationFeatures Any existing location features
 * @param nextFeatureId A unique and iterable ID that will be used to create a unique ID for the map feature.
 * @param nextGroupId A unique and iterable ID that will be used to create a unique ID for the map feature group.
 * @returns
 */
export function getNoticeFeatureGroups(
    locationFeatures: LocationFeatureDto[],
    nextFeatureId: number,
    nextGroupId = 0,
    colour: string
): { nextId: number; features: FeatureGroup[] } {
    const groups = groupByMapFeature(locationFeatures, nextFeatureId);
    return {
        nextId: locationFeatures.length + nextFeatureId,
        features: [
            {
                id: nextGroupId + 0,
                name: 'Areas',
                type: 'Polygon',
                canAdd: true,
                minimumNumber: 0,
                maximumNumber: null,
                categories: [
                    {
                        id: LocationFeatureDto.Type.AREA_OF_INTEREST,
                        name: 'Area of Interest'
                    },
                    {
                        id: LocationFeatureDto.Type.DANGER,
                        name: 'Danger'
                    },
                    {
                        id: LocationFeatureDto.Type.NO_FLY,
                        name: 'No Fly Area'
                    }
                ],
                existingFeatures: groups.areas,
                styles: {
                    fill: [
                        {
                            paint: {
                                'fill-color': '#ff1234'
                            },
                            filter: ['!', ['has', 'category-id']]
                        },
                        {
                            paint: {
                                'fill-pattern': `NOTICE-DANGER-${colour}`,
                                'fill-opacity': 1
                            },

                            filter: [
                                '==',
                                ['get', 'category-id'],
                                LocationFeatureDto.Type.DANGER
                            ]
                        },
                        {
                            paint: {
                                'fill-pattern': `NOTICE-NO_FLY-${colour}`,
                                'fill-opacity': 1
                            },

                            filter: [
                                '==',
                                ['get', 'category-id'],
                                LocationFeatureDto.Type.NO_FLY
                            ]
                        },
                        {
                            paint: {
                                'fill-pattern': `NOTICE-AREA_OF_INTEREST-${colour}`,
                                'fill-opacity': 1
                            },
                            filter: [
                                '==',
                                ['get', 'category-id'],
                                LocationFeatureDto.Type.AREA_OF_INTEREST
                            ]
                        }
                    ],
                    line: [
                        {
                            paint: {
                                'line-color': `#${colour}`,
                                'line-width': 3,
                                'line-opacity': 1
                            },

                            filter: [
                                '==',
                                ['get', 'category-id'],
                                LocationFeatureDto.Type.DANGER
                            ]
                        },
                        {
                            paint: {
                                'line-color': `#${colour}`,
                                'line-width': 3,
                                'line-opacity': 1
                            },

                            filter: [
                                '==',
                                ['get', 'category-id'],
                                LocationFeatureDto.Type.NO_FLY
                            ]
                        },
                        {
                            paint: {
                                'line-color': `#${colour}`,
                                'line-width': 3,
                                'line-opacity': 1
                            },

                            filter: [
                                '==',
                                ['get', 'category-id'],
                                LocationFeatureDto.Type.AREA_OF_INTEREST
                            ]
                        }
                    ],
                    symbol: [
                        {
                            layout: {
                                'text-field': '{name}',
                                'symbol-placement': 'line',
                                'text-anchor': 'bottom',
                                'symbol-spacing': 150,
                                'text-max-angle': 35,
                                'text-size': 12,
                                'text-letter-spacing': 0.1,
                                'text-font': [
                                    'Roboto Bold',
                                    'Arial Unicode MS Regular'
                                ]
                            },
                            paint: {
                                'text-halo-color': 'hsl(0, 100%, 100%)',
                                'text-halo-width': 1,
                                'text-halo-blur': 1
                            }
                        }
                    ]
                }
            },
            {
                id: nextGroupId + 3,
                name: 'Corridors',
                type: 'AreaLineString',
                canAdd: true,
                minimumNumber: 0,
                maximumNumber: null,
                categories: [
                    {
                        id: LocationFeatureDto.Type.OFFSET_NO_FLY_AREA,
                        name: 'No Fly Area'
                    },
                    {
                        id: LocationFeatureDto.Type.OFFSET_DANGER_AREA,
                        name: 'Danger'
                    }
                ],
                existingFeatures: groups.corridors,
                styles: {
                    fill: [
                        {
                            paint: {
                                'fill-color': '#ff1234'
                            },
                            filter: ['!', ['has', 'category-id']]
                        },
                        {
                            paint: {
                                'fill-color': '#990000',
                                'fill-opacity': 0.4
                            },
                            filter: [
                                '==',
                                ['get', 'category-id'],
                                LocationFeatureDto.Type.OFFSET_FLIGHT_AREA
                            ]
                        },
                        {
                            paint: {
                                'fill-pattern': 'no-fly',
                                'fill-opacity': 0.3
                            },

                            filter: [
                                '==',
                                ['get', 'category-id'],
                                LocationFeatureDto.Type.OFFSET_DANGER_AREA
                            ]
                        },
                        {
                            paint: {
                                'fill-pattern': 'danger-area',
                                'fill-opacity': 1
                            },

                            filter: [
                                '==',
                                ['get', 'category-id'],
                                LocationFeatureDto.Type.OFFSET_NO_FLY_AREA
                            ]
                        }
                    ],
                    line: [
                        {
                            paint: {
                                'line-color': '#FF0000',
                                'line-width': 3,
                                'line-opacity': 1,
                                'line-dasharray': [5, 5]
                            },

                            filter: ['==', ['geometry-type'], 'LineString']
                        },
                        {
                            paint: {
                                'line-color': '#990000',
                                'line-width': 3,
                                'line-opacity': 1
                            },

                            filter: [
                                'all',
                                [
                                    '==',
                                    ['get', 'category-id'],
                                    LocationFeatureDto.Type.OFFSET_FLIGHT_AREA
                                ],
                                ['==', ['geometry-type'], 'Polygon']
                            ]
                        },
                        {
                            paint: {
                                'line-color': '#fecb1c',
                                'line-width': 3,
                                'line-opacity': 0.5
                            },

                            filter: [
                                'all',
                                [
                                    '==',
                                    ['get', 'category-id'],
                                    LocationFeatureDto.Type.OFFSET_DANGER_AREA
                                ],
                                ['==', ['geometry-type'], 'Polygon']
                            ]
                        },
                        {
                            paint: {
                                'line-width': 3,
                                'line-opacity': 0.5
                            },

                            filter: [
                                'all',
                                [
                                    '==',
                                    ['get', 'category-id'],
                                    LocationFeatureDto.Type.OFFSET_NO_FLY_AREA
                                ],
                                ['==', ['geometry-type'], 'Polygon']
                            ]
                        }
                    ],
                    symbol: [
                        {
                            layout: {
                                'text-field': '{name}',
                                'symbol-placement': 'line',
                                'text-anchor': 'bottom',
                                'symbol-spacing': 150,
                                'text-max-angle': 35,
                                'text-size': 12,
                                'text-letter-spacing': 0.1,
                                'text-font': [
                                    'Roboto Bold',
                                    'Arial Unicode MS Regular'
                                ]
                            },
                            paint: {
                                'text-halo-color': 'hsl(0, 100%, 100%)',
                                'text-halo-width': 1,
                                'text-halo-blur': 1
                            }
                        }
                    ]
                }
            }
        ]
    };
}
